const { getInsertProperties, queryPaginationTransform } = require('../../tools/sql-tools')
const { nanoid } = require('nanoid')

/**
 * @typedef {Object} PriceEntry
 * @property {string} productId
 * @property {number} sellPrice
 * @property {string} effectiveDate
*/

/**
 * @typedef {Object} Service
 * @property {string} id
 * @property {string} fsid
*/

class LocationPrices {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
    this.LIMIT = 25
  }

  async getProgramProducts (programIds, isWarehouseUser) {
    if (!programIds || !programIds.length) {
      return []
    }

    const { placeholders, values } = programIds.reduce((acc, id, index) => {
      acc.placeholders += `d.id ilike $${index + 1}`
      if (index < programIds.length - 1) {
        acc.placeholders += ' or '
      }
      acc.values.push(`%configuration:${id}:service%`)
      return acc
    }, {placeholders: '', values: []})

    const warehouseClause = isWarehouseUser ? ` where p.service ilike '%warehouse%' ` : ` where p.service not ilike '%warehouse%' `
    const { rows: [row] } = await this.pgConnection.query(`
      SELECT
        coalesce(
          (
            select jsonb_agg(p)
            from (
              with product_per_service as (
                select
                  d.doc ->> 'service' as service,
                  p.product
                from couchdb.db_integrated_data d
                cross join lateral jsonb_array_elements(coalesce(d.doc->'products', '[]'::jsonb)) as p(product)
                where (${placeholders})
              )
              select
                product as "productId",
                coalesce(jsonb_agg(json_build_object('id', s.id, 'fsid', p.service)), '[]'::jsonb) as services
              from product_per_service p
              left join avocado.data_service s on p.service = s.fsid 
              ${warehouseClause}
              group by 1
            ) p
          ), '[]'::jsonb) as products
    `, values)
    return row.products
  }

  /**
   * @param {string} locationFsid
   * @param {string} productId
   * @param {string} userFsid
   * @param {number} page
   * @param {string} service
  */
  async getProductSellPrices (locationFsid, productId, userFsid, page, service) {
    const sku = productId.replace('product:', '')
    const params = [locationFsid, sku]
    let serviceFilter = ''
    if (service) {
      serviceFilter = service ? ' and s.fsid = $3 ' : ''
      params.push(service)
    }
    const paginatedQuery = queryPaginationTransform({
      baseQuery: `
        with base_prices as (
          select
            l.fsid as location_fsid,
            l.name_of_pharmacy,
            ph.*,
           row_number() over (partition by sku, location_id order by date) as price_order
          from avocado.data_location l
          left join real.location_price_history ph on ph.location_id = l.uuid
          left join avocado.data_service s on s.id = ph.service_id
          where l.fsid = $1 and ph.sku = $2 ${serviceFilter}
        ),
        sell_prices as (
          select
            tp.id,
            tp.date as "effectiveDate",
            '${userFsid}' = tp.updated_by as "updatedByCurrentUser",
            concat('product:', tp.sku) as "productId",
            coalesce(u."name", regexp_replace(tp.updated_by, 'org.couchdb.user:', '' )) as "updatedBy",
            json_build_object(
              'fsid', tp.location_fsid,
              'fullName', tp.name_of_pharmacy
            ) as "location",
            json_build_object(
              'from', fp.sellprice,
              'to', tp.sellprice 
            ) as "sellPrice",
            json_build_object(
              'id', s.id,
              'fsid', s.fsid,
              'name', s."name"
            ) as "service",
            tp.created_at as "createdAt",
            tp.updated_at as "updatedAt"
          from base_prices tp
          left join base_prices fp on
            tp.sku = fp.sku and 
            tp.location_id = fp.location_id and
            tp.price_order = fp.price_order + 1
          left join avocado.data_service s on s.id = tp.service_id
          left join avocado.data_user u on u.fsid = tp.updated_by 
        )
      `,
      cteName: 'sell_prices',
      orderByClause: 'order by "effectiveDate" desc',
      pageNo: page,
      maxLimit: this.LIMIT
    })

    const { rows: [data] } = await this.pgConnection.query(paginatedQuery, params)
    return data
  }

  /**
   * @param {string} locationFsid
   * @param {string} userFsid
   * @param {PriceEntry[]} entry
   * @param {Object<string, Service[]>} productServices
  */
  async uploadSellPrices (locationFsid, userFsid, entry, productServices) {
    const error = new Error()
    let startIndex = 2
    const {args, params} = entry.reduce((acc, curr, index) => {
      acc.args += `(ph.sku = $${index + startIndex} and ph.date = $${index + startIndex + 1})`
      if (index < entry.length - 1) {
        acc.args += ' or '
      }
      acc.params.push(curr.productId.replace('product:', ''), curr.effectiveDate)
      startIndex += 1
      return acc
    }, { args: '', params: [locationFsid] })

    const { rows: [existing] } = await this.pgConnection.query(`
      with base_location as (
        select
          uuid
        from avocado.data_location
        where fsid = $1
      )
      select
        bl.uuid as location_id,
        coalesce(
          (
            select jsonb_agg(p)
            from (
              select
                concat('product:', ph.sku) as "productId",
                ph.date as "effectiveDate",
                s.fsid as "serviceFsid"
              from real.location_price_history ph
              left join avocado.data_service s on s.id = ph.service_id
              where ph.location_id = bl.uuid and (${args})
              group by 1, 2, 3
            ) p
          ),
          '[]'::jsonb
        ) as "sellPrices"
      from base_location bl
    `, params)

    if (!existing) {
      error.message = 'User not tied to a location'
      error.status = 401
      throw error
    }

    if (existing.sellPrices.length > 0) {
      error.message = 'Sell price entries already exist for provided products effective dates'
      error.status = 409
      error.errors = existing.sellPrices
      throw error
    }

    const txnRef = nanoid(10)
    const parsedRows = []
    // Create price entries for every service associated with the product
    for (const price of entry) {
      const sku = price.productId.replace('product:', '')
      for (const service of productServices[price.productId]) {
        parsedRows.push({
          location_id: existing.location_id,
          sku,
          sellprice: price.sellPrice,
          date: price.effectiveDate,
          txn_ref: txnRef,
          updated_by: userFsid,
          service_id: service.id
        })
      }
    }

    const { columns, values, placeholders } = getInsertProperties(parsedRows)
    const { rows } = await this.pgConnection.query(`
      insert into real.location_price_history (${columns})
      values ${placeholders}
      on conflict (sku, date, service_id, location_id) do nothing
      returning *
    `, values)
    return rows
  }

  async getSellPriceList (locationFsid, date) {
    const params = [locationFsid]
    let dateFilter = ''
    if (!date) {
      dateFilter = ' and date <= CURRENT_DATE '
    } else {
      dateFilter = ` and date <= $2::timestamptz `
      params.push(date)
    }

    const { rows: [priceList] } = await this.pgConnection.query(`
      with base_prices as (
        select
          ph.*,
          row_number() over (partition by sku, location_id order by date desc) as price_order
        from avocado.data_location l
        left join real.location_price_history ph on l.uuid = ph.location_id 
        where l.fsid = $1 ${dateFilter}
      )
      select
        $1 as "locationFsid",
        ${date ? '$2::timestamptz' : 'CURRENT_DATE'} as "referenceDate",
        coalesce(
          (
            select jsonb_agg(p)
            from (
              select
                id,
                concat('product:', sku) as "productId",
                sellprice::float as "sellPrice",
                date as "effectiveDate"
              from base_prices
              where price_order = 1
            ) p
          ),
          '[]'::jsonb
        ) as "sellPrices"
    `, params)
    return priceList
  }
}

module.exports = { LocationPrices }
